// react
import React, { useEffect, useState } from 'react'

// components
import {
  BUTTON_THEME,
  Button,
  Chip,
  CircularLoader,
  Input,
  Select,
  Switch,
} from '@st-operations/uikit/src/v2/index.js'
import Header from '_components/header/index.jsx'

// contexts
import { usePDPEnvironment } from '../../../contexts/PDPEnvContext'

// lib
import { logErrorAsync } from '_lib/utility'
import sendAnalytics from '_root/lib/sendAnalytics.js'

// services
import { getPdpOrganizations, getRule, updateRule } from '_services/pdp-audience-notification-rules'

// pages
import { DEFAULT_ENV } from '../../predactiv-admin/helpers'

// styles
import '../styles.scss'

const ENTITY = 'pdp-audience-notification-rules'

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

const PdpAudienceNotificationRulePage = (props) => {
  const { id } = props.match.params

  // The provider only wraps the Predactiv Admin routes; elsewhere fall back to
  // the same default those pages start from.
  const { pdpEnvironment } = usePDPEnvironment() || {}
  const env = pdpEnvironment || DEFAULT_ENV

  const [rule, setRule] = useState(null)
  const [organizations, setOrganizations] = useState([])
  const [values, setValues] = useState({ name: '', enabled: true, organizations: [], emails: [] })
  const [emailDraft, setEmailDraft] = useState('')
  const [hasChanges, setHasChanges] = useState(false)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!window._isShareThisUser) return

    getRule(id)
      .then((item) => {
        setRule(item)
        setValues({
          name: item.name || '',
          enabled: item.enabled !== false,
          organizations: item.organizations || [],
          emails: item.emails || [],
        })
        sendAnalytics('read', ENTITY, item)
      })
      .catch((error) => {
        logErrorAsync(error)
        toast.error(error.message)
      })
  }, [id])

  useEffect(() => {
    if (!window._isShareThisUser) return

    getPdpOrganizations(env)
      .then((data) =>
        setOrganizations(
          (data?.items || []).map((organization) => ({
            _id: `${organization._id}`,
            name: organization.name,
          }))
        )
      )
      .catch((error) => {
        logErrorAsync(error)
        toast.error(`Could not load organizations: ${error.message}`)
      })
  }, [env])

  const change = (field, value) => {
    setValues((current) => ({ ...current, [field]: value }))
    setHasChanges(true)
  }

  const addEmail = () => {
    const email = emailDraft.trim()
    if (!email) return

    if (!EMAIL.test(email)) {
      toast.error(`${email} is not a valid email address.`)
      return
    }

    if (values.emails.some((existing) => existing.toLowerCase() === email.toLowerCase())) {
      setEmailDraft('')
      return
    }

    change('emails', [...values.emails, email])
    setEmailDraft('')
  }

  const removeEmail = (email) => {
    change(
      'emails',
      values.emails.filter((existing) => existing !== email)
    )
  }

  const exit = () => props.history.push(`/${ENTITY}`)

  const save = () => {
    if (saving) return

    if (!values.emails.length) {
      toast.error('Add at least one email address, or the rule will notify nobody.')
      return
    }

    setSaving(true)
    const updated = { ...rule, ...values }

    updateRule(id, updated)
      .then(() => {
        toast.success('Update successful!')
        sendAnalytics('update', ENTITY, updated)
        exit()
      })
      .catch((error) => {
        logErrorAsync(error)
        toast.error(error.message)
        setSaving(false)
      })
  }

  // The sidebar already hides this page from client users; this covers a
  // direct URL.
  if (!window._isShareThisUser) {
    return (
      <div className="pdp-notification-rules">
        <Header title="PDP Audience Notifications" />
        <p className="pdp-notification-rules__description">
          These settings are available to ShareThis users only.
        </p>
      </div>
    )
  }

  if (!rule) {
    return (
      <div className="pdp-notification-rules__loading">
        <CircularLoader />
      </div>
    )
  }

  return (
    <div id="pdp-audience-notification-rule-page" className="pdp-notification-rule">
      <Header
        title={values.name}
        back={exit}
        actions={[
          { children: 'Exit', onClick: exit, theme: BUTTON_THEME.GHOST },
          { children: 'Save', onClick: save, disabled: !hasChanges || saving },
        ]}
      />

      <div className="pdp-notification-rule__body">
        <p className="pdp-notification-rule__description">
          When a PDP audience is approved, every enabled rule matching its organization contributes
          its recipients to the notification email.
        </p>

        <div className="pdp-notification-rule__fields">
          <div className="pdp-notification-rule__field">
            <Input
              label="Rule Name"
              value={values.name}
              onChange={(value) => change('name', value)}
            />
          </div>

          <div className="pdp-notification-rule__field pdp-notification-rule__toggle">
            <Switch checked={values.enabled} onChange={() => change('enabled', !values.enabled)} />
            <div>
              <span className="pdp-notification-rule__label">Enabled</span>
              <p className="pdp-notification-rule__hint">
                <Choose>
                  <When condition={values.enabled}>
                    This rule is applied when an audience is approved.
                  </When>
                  <Otherwise>Turned off — this rule notifies nobody.</Otherwise>
                </Choose>
              </p>
            </div>
          </div>

          <div className="pdp-notification-rule__field">
            <Select
              label="Organizations"
              description="Leave empty to match every PDP organization"
              options={organizations}
              value={values.organizations}
              onChange={(selected) => change('organizations', selected || [])}
              isMulti={true}
              isSearchable={true}
              useReactSelect={true}
              isClearable={false}
            />
          </div>

          <div className="pdp-notification-rule__field">
            <span className="pdp-notification-rule__label">Recipients</span>
            <p className="pdp-notification-rule__hint">
              Each address is emailed once, no matter how many rules name it.
            </p>

            <div className="pdp-notification-rule__email-row">
              <Input
                placeholder="name@sharethis.com"
                value={emailDraft}
                onChange={setEmailDraft}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault()
                    addEmail()
                  }
                }}
              />
              <Button
                theme={BUTTON_THEME.SECONDARY}
                onClick={addEmail}
                disabled={!emailDraft.trim()}
              >
                Add
              </Button>
            </div>

            <Choose>
              <When condition={values.emails.length}>
                <div className="pdp-notification-rule__chips">
                  <For each="email" of={values.emails}>
                    <Chip key={email} value={email} onClick={removeEmail} />
                  </For>
                </div>
              </When>
              <Otherwise>
                <p className="pdp-notification-rule__empty">
                  No recipients yet — this rule will notify nobody.
                </p>
              </Otherwise>
            </Choose>
          </div>
        </div>
      </div>
    </div>
  )
}

// export
export default PdpAudienceNotificationRulePage
